import { ButtonHTMLAttributes, ReactNode } from 'react';
import { useTheme } from '../../theme';

export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  /** Cor de fundo do botão (padrão: tema do E-Learning) */
  backgroundColor?: string;
  /** Cor da fonte (padrão: tema do E-Learning) */
  color?: string;
  /** Tamanho do botão e da fonte: pequeno, médio ou grande */
  size?: ButtonSize;
}

export default function Button({
  children,
  backgroundColor,
  color,
  size = 'medium',
  style,
  ...rest
}: ButtonProps) {
  const theme = useTheme();
  const defaultBackgroundColor = theme.palette.primary.main;
  const defaultColor = theme.palette.primary.contrastText;

  const sizeStyles: Record<
    ButtonSize,
    { padding: string; fontSize: string; minHeight: string }
  > = {
    small: {
      padding: `${theme.spacing.buttonSmallY} ${theme.spacing.buttonSmallX}`,
      fontSize: theme.typography.fontSize.base,
      minHeight: theme.spacing.xl,
    },
    medium: {
      padding: `${theme.spacing.sm} ${theme.spacing.buttonMediumX}`,
      fontSize: theme.typography.fontSize.md,
      minHeight: theme.spacing['2xl'],
    },
    large: {
      padding: `${theme.spacing.buttonLargeY} ${theme.spacing.buttonLargeX}`,
      fontSize: theme.typography.fontSize.lg,
      minHeight: theme.spacing['3xl'],
    },
  };

  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.semibold,
    border: 'none',
    outline: 'none',
    borderRadius: theme.shape.borderRadius.md,
    cursor: 'pointer',
    transition: 'opacity 0.2s, box-shadow 0.2s, transform 0.1s',
  };

  return (
    <button
      type="button"
      className="e-learning-button"
      style={{
        ...baseStyle,
        ...sizeStyles[size],
        backgroundColor: backgroundColor ?? defaultBackgroundColor,
        color: color ?? defaultColor,
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}
